<template>
  <va-form
    :id="isEdit ? header.id : ''"
    ref="form"
    :item="header"
    v-model="form"
    :before-save="beforeSave"
    :after-save="handleAfterSave"
    :required-fields-set="requiredFieldsSet"
    :modify-before-send="modifyBeforeSend">
    <Modal
      width="448"
      :value="displayVersionHistoryModal"
      :title="$t('resources.headers.history_modal_title')"
      :cta-text="$t('resources.pages.page_history_modal_cta_text')"
      :cta-disabled="!selectedVersion"
      :cta-loading="changeVersionLoading"
      @confirm="confirmVersionChange"
      @cancel="cancelVersionChange"
      actions-border>
      <v-select
        class="spacing-top--20"
        v-model="selectedVersion"
        :items="versionsOptions"
        :label="$t('resources.headers.version_select_label')"
        :placeholder="$t('resources.headers.version_select_placeholder')"
        persistent-placeholder
        :menu-props="{ offsetY: true, offsetOverflow: true }"
        hide-details="auto">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
        <template v-slot:item="{ item }">
          <div class="v-list-item__content">
            <div class="v-list-item__title">{{ item.text }}</div>
          </div>
          <div v-if="item.disabled" class="ml-auto">
            <v-chip class="in-use-chip" :color="'subtitle'" outlined :text-color="'subtitle'">
              {{ $t("resources.pages.version_in_use") }}
            </v-chip>
          </div>
        </template>
      </v-select>
    </Modal>
    <v-card class="spacing-bottom--24">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>Details</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">
            {{ $t("resources.headers.headers_details_block_subtitle") }}
          </v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-checkbox
            color="primary"
            v-model="form.isDefault"
            :label="$t('resources.headers.set_as_default')"></v-checkbox>
        </v-card-actions>
      </div>
      <div v-if="form.updatedAt" class="spacing-bottom--16">
        <div class="updated-at-label">
          {{ $t("resources.headers.fields.last_updated") }}
        </div>
        <div class="d-flex">
          <div class="updated-at-time">
            {{ getFullDateAndTime(form.updatedAt) }}
            {{ form.updatedBy && `by ${form.updatedBy.firstName} ${form.updatedBy.lastName}` }}
          </div>
          <div
            v-if="isEdit && versionsOptions.length"
            class="view-history ml-auto"
            @click="displayVersionHistoryModal = true">
            {{ $t("resources.pages.view_history") }}
          </div>
        </div>
      </div>
      <va-text-input
        required
        v-model="form.name"
        source="name"
        class="spacing-bottom--16"
        :label="$t('resources.headers.fields.name')"
        :placeholder="$t('resources.headers.headers_name_placeholder')"></va-text-input>

      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        class="spacing-bottom--16"
        required
        :placeholder="$t('resources.headers.headers_domain_placeholder')"
        :label="$t('resources.headers.domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.domain">
        <div class="page-label-row">
          <div class="page-label">
            {{ $t("resources.headers.fields.page") }}
          </div>
          <v-btn
            text
            color="primary"
            class="chip-action-button"
            @click="showAddPageDialog = true"
            :ripple="false">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t("resources.headers.add_page") }}
          </v-btn>
        </div>
        <div class="chip-container" v-if="form.pages && form.pages.length > 0">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page['@id']">
            <v-icon small left @click.stop="openPageEdit(page)">mdi-open-in-new</v-icon>
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
        <PagePickerModal
          v-model="showAddPageDialog"
          :pages="form.pages || []"
          :domain="form.domain"
          :title="$t('resources.headers.add_page_title')"
          :placeholder="$t('resources.headers.add_page_placeholder')"
          :note="$t('resources.headers.add_page_reassign_warning')"
          in-use-field="currentHeader"
          :current-resource-iri="form['@id']"
          :in-use-message="$t('resources.headers.add_page_in_use_message')"
          @confirm="syncPages" />
      </div>

      <v-select
        v-model="form.template"
        :label="$t('resources.headers.desktop_layout')"
        :items="desktopTemplateOptions"
        @change="headerTemplateChange(form.template)"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <v-select
        v-model="form.mobileTemplate"
        v-if="showMobileLayoutField && form.mobileTemplate"
        :label="$t('resources.headers.mobile_layout')"
        :items="HEADER_MOBILE_TEMPLATES"
        @change="mobileTemplateChange"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <va-text-input
        v-if="showMobileBurgerLinkField"
        v-model="form.mobileBurgerLink"
        source="mobileBurgerLink"
        class="spacing-bottom--16"
        :label="$t('resources.headers.mobile_burger_link')"
        :placeholder="$t('resources.headers.mobile_burger_link_placeholder')"
        :rules="[v => !v || !!urlValidation(v) || $t('generic.validation.url')]"></va-text-input>

      <v-select
        v-model="form.logoPosition"
        v-if="showMobileLayoutField && form.logoPosition"
        :label="$t('resources.headers.mobile_layout')"
        :items="HEADER_LOGO_POSITIONS"
        :menu-props="{
          offsetOverflow: true,
          offsetY: true,
        }">
        <template v-slot:append>
          <v-icon>mdi-chevron-down</v-icon>
        </template>
      </v-select>

      <VaImageDragAndDropInput
        class="spacing-bottom--16"
        :item-id="form.logo"
        :label="$t('resources.headers.default_logo')"
        @update:photo="updateHeaderLogo"></VaImageDragAndDropInput>

      <VaImageDragAndDropInput
        class="spacing-bottom--16"
        :item-id="form.mobileLogo"
        :label="$t('resources.headers.mobile_logo')"
        @update:photo="updateHeaderMobileLogo"></VaImageDragAndDropInput>

      <va-tinymce-input
        :key="versionKey"
        v-if="showCustomContentField"
        source="customContent"
        :title="$t('resources.headers.content')"
        :current-content="form.customContent"
        :editor-height="320"
        :stripped-down="true"
        :disable-image-filters="true"
        @update="updateContent" />

      <va-tinymce-input
        :key="versionKey"
        class="spacing-top--16"
        v-if="showMobileCustomContentField"
        source="mobileCustomContent"
        :title="$t('resources.headers.mobile_content')"
        :current-content="form.mobileCustomContent"
        :editor-height="320"
        :stripped-down="true"
        :disable-image-filters="true"
        @update="updateMobileContent" />
    </v-card>
    <v-card v-if="showMenuField" class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.headers.header_items_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--11">
        {{ $t("resources.headers.header_items_block_subtitle") }}
      </v-card-subtitle>
      <va-array-input
        :key="versionKey"
        v-model="form.menu.menuItems"
        v-slot="props"
        source="menu.menuItems"
        :label="false"
        :hide-details="'auto'"
        :add-button-text="$t('generic.add_menu_link')">
        <v-select
          v-model="form.menu.menuItems[props.index].type"
          source="type"
          persistent-placeholder
          :placeholder="$t('resources.headers.headers_menu_item_type_placeholder')"
          :hide-details="'auto'"
          :label="$t('resources.headers.item_type')"
          :items="MENU_ITEM_TYPES"
          @change="changeMenuType(props.index)"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-text-input
          v-model="form.menu.menuItems[props.index].text"
          class="spacing-horizontal--16"
          :label="$t('resources.headers.item_title')"
          :placeholder="$t('resources.headers.headers_menu_item_title_placeholder')"
          required
          v-bind="props"
          source="text"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type === 3"
          v-model="form.menu.menuItems[props.index].category"
          :label="$t('resources.headers.page_category')"
          source="category"
          :placeholder="$t('resources.headers.headers_menu_page_category_placeholder')"
          required
          :filter="{ ...resourcesFilter }"
          v-bind="props"
          reference="page_categories"
          item-value="@id"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <va-text-input
          v-if="form.menu.menuItems[props.index].type === 2"
          v-model="form.menu.menuItems[props.index].link"
          :label="$t('resources.headers.external_link')"
          :placeholder="$t('resources.headers.headers_menu_item_link_placeholder')"
          required
          :rules="[v => !!urlValidation(v) || $t('generic.validation.url')]"
          v-bind="props"
          source="link"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type !== 3 && form.menu.menuItems[props.index].type !== 2"
          v-model="form.menu.menuItems[props.index].page"
          v-bind="props"
          :label="$t('resources.headers.existing_page')"
          :placeholder="$t('resources.headers.headers_menu_page_placeholder')"
          :filter="{ ...resourcesFilter, status: [1, 2] }"
          source="page"
          reference="pages"
          item-value="@id"
          required
          searchQuery="headline"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <v-select
          class="spacing-left--16"
          v-model="form.menu.menuItems[props.index].action"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          :hide-details="'auto'"
          :label="$t('generic.open_in')"
          :items="MENU_TAB_TYPES">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-number-input
          style="display: none"
          disabled
          :value="(form.menu.menuItems[props.index].position = props.index + 1)"
          required
          v-bind="props"
          source="position"></va-number-input>
      </va-array-input>
    </v-card>

    <v-card>
      <v-card-title>{{ $t("resources.headers.colors_title") }} </v-card-title>
      <v-card-subtitle>{{ $t("resources.headers.colors_subtitle") }}</v-card-subtitle>

      <div class="d-flex">
        <v-card-text>
          <va-color-picker
            @is-hovered="e => (isBackgroundFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.headers.background_color')"
            v-model="form.backgroundColor">
          </va-color-picker>
          <va-color-picker
            @is-hovered="e => (isBorderFieldHovered = e)"
            class="mb-4"
            :title="$t('resources.headers.border_color')"
            v-model="form.borderColor">
          </va-color-picker>
          <div class="d-flex">
            <va-color-picker
              v-if="HEADER_BLOG_TEMPLATES_VALUES.includes(form.template)"
              @is-hovered="e => (isMenuItemTextFieldHovered = e)"
              class="mb-4 mr-4"
              :title="$t('resources.headers.menu_item_color')"
              v-model="form.menuItemColor">
            </va-color-picker>
            <va-color-picker
              v-if="HEADER_BLOG_TEMPLATES_VALUES.includes(form.template)"
              @is-hovered="e => (isMenuItemHoverFieldHovered = e)"
              @is-active="e => (isMenuItemHoverFieldActive = e)"
              class="mb-4"
              :title="$t('resources.headers.hover_color')"
              v-model="form.hoverColor">
            </va-color-picker>
          </div>
          <va-color-picker
            v-if="HEADER_BLOG_TEMPLATES_VALUES.includes(form.template)"
            @is-hovered="e => (isMenuItemBgHoverFieldHovered = e)"
            @is-active="e => (isMenuItemBgHoverFieldActive = e)"
            class="mb-4"
            :title="$t('resources.headers.hover_background_color')"
            v-model="form.hoverBackgroundColor">
          </va-color-picker>
        </v-card-text>

        <v-card-text class="preview-box">
          <label class="preview-box-label"> {{ $t("generic.preview_box.label") }} </label>
          <div class="preview-box-container">
            <div class="preview-box__template">
              <div class="label">{{ $t("generic.preview_box.desktop_layout") }}</div>

              <div
                class="preview-box__template-wrapper"
                :class="{ 'bg-mask': isBackgroundFieldHovered, 'border-mask': isBorderFieldHovered }"
                :style="{ 'background-color': form.backgroundColor, 'border-bottom-color': form.borderColor }">
                <div class="preview-box__template-logo">{{ $t("generic.preview_box.logo") }}</div>
                <div class="preview-box__template-menu">
                  <div
                    class="preview-box__template-menu__item"
                    :class="{
                      'menu-item-mask': isMenuItemTextFieldHovered || isMenuItemHoverFieldHovered,
                    }"
                    :style="{ 'background-color': isMenuItemHoverFieldActive ? form.hoverColor : form.menuItemColor }">
                    <img
                      v-if="isMenuItemHoverFieldHovered || isMenuItemHoverFieldActive"
                      class="preview-box__template-menu__item__cursor"
                      :src="cursorPointerIcon"
                      alt="cursor pointer icon" />
                    <div
                      v-if="isMenuItemBgHoverFieldHovered || isMenuItemBgHoverFieldActive"
                      class="menu-bg-mask"
                      :class="{ hovered: isMenuItemBgHoverFieldHovered }"
                      :style="{ 'background-color': form.hoverBackgroundColor }">
                      <div
                        class="preview-box__template-menu__item preview-box__template-menu__item--active"
                        :style="{
                          'background-color': isMenuItemBgHoverFieldHovered ? form.menuItemColor : form.hoverColor,
                        }">
                        <img
                          class="preview-box__template-menu__item__cursor"
                          :src="cursorPointerIcon"
                          alt="cursor pointer icon" />
                      </div>
                    </div>
                  </div>
                  <div
                    class="preview-box__template-menu__item"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                  <div
                    class="preview-box__template-menu__item"
                    :class="{ 'menu-item-mask': isMenuItemTextFieldHovered }"
                    :style="{ 'background-color': form.menuItemColor }"></div>
                </div>
              </div>
            </div>

            <div class="preview-box__template preview-box__template--mobile">
              <div class="label">{{ $t("generic.preview_box.mobile_layout") }}</div>

              <div
                class="preview-box__template-wrapper"
                :class="{ 'bg-mask': isBackgroundFieldHovered, 'border-mask': isBorderFieldHovered }"
                :style="{ 'background-color': form.backgroundColor, 'border-bottom-color': form.borderColor }">
                <div class="preview-box__template-logo">{{ $t("generic.preview_box.logo") }}</div>
                <div class="preview-box__template-menu">
                  <img class="preview-box__template-menu__burger" :src="mobileBurgerIcon" alt="mobile burger icon" />
                </div>
              </div>
            </div>
          </div>
          <div class="preview-box-description" v-html="$t('generic.preview_box.description')"></div>
        </v-card-text>
      </div>
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Components
  import VaImageDragAndDropInput from "../custom/VaImageDragAndDropInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  import PagePickerModal from "@/components/custom/PagePickerModal";
  import Modal from "@/components/custom/Modal";

  // Mixins
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import crudFormMixin from "@/mixins/crudFormMixin";
  import changeMenuType from "@/mixins/changeMenuTypeMethod";
  import providedDomain from "@/mixins/providedDomain";
  import pagesAttachment from "@/mixins/pagesAttachment";

  // Constants
  import {
    MENU_ITEM_TYPES,
    HEADER_LOGO_POSITIONS,
    HEADER_TEMPLATES_OLD,
    HEADER_TEMPLATES,
    HEADERS_TEMPLATES_UPDATES_DATE,
    HEADER_MOBILE_TEMPLATES,
    HEADER_BLOG_TEMPLATES_VALUES,
    HEADER_CONTENT_ONLY_TEMPLATE,
    HEADER_LOGO_LEFT_BURGER_TEMPLATE,
    MENU_TAB_TYPES,
  } from "../../constant/index";

  // Utils
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { parseToHtml } from "@/utility/parseToHtml";
  import { formatMenuItems, formatRestoredMenuItems } from "@/utility/formatMenuItems";
  import { urlValidation } from "@/utility/urlValidation";
  import { removeAlpha } from "@/utility/removeAlpha";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { getFullDateAndTime } from "@core/utils/filter";

  const mobileBurgerIcon = require("@/assets/images/svg/mobile-burger.svg");
  const cursorPointerIcon = require("@/assets/images/svg/cursor-pointer.svg");

  export default {
    name: "HeaderForm",
    components: {
      VaImageDragAndDropInput,
      VaColorPicker,
      RequiredFieldText,
      PagePickerModal,
      Modal,
    },
    mixins: [crudFormMixin, ConnectedDomainsList, changeMenuType, providedDomain, pagesAttachment],
    props: {
      header: {
        type: Object,
        default: () => {},
      },
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        urlValidation,
        ability: definedAbility(),
        mobileBurgerIcon,
        cursorPointerIcon,
        HEADER_LOGO_POSITIONS,
        HEADER_TEMPLATES_OLD,
        HEADERS_TEMPLATES_UPDATES_DATE,
        HEADER_TEMPLATES,
        MENU_ITEM_TYPES,
        HEADER_MOBILE_TEMPLATES,
        MENU_TAB_TYPES,
        requiredFormFields: ["name", "domain"],
        form: {
          customContent: "",
        },
        showMobileLayoutField: true,
        showMenuField: true,
        showCustomContentField: true,
        showMobileCustomContentField: false,
        isBackgroundFieldHovered: false,
        isBorderFieldHovered: false,
        isMenuItemTextFieldHovered: false,
        isMenuItemHoverFieldHovered: false,
        isMenuItemHoverFieldActive: false,
        isMenuItemBgHoverFieldHovered: false,
        isMenuItemBgHoverFieldActive: false,
        desktopTemplateOptions: HEADER_TEMPLATES,
        HEADER_BLOG_TEMPLATES_VALUES,
        oldDomain: "",
        getFullDateAndTime,
        versions: [],
        versionsOptions: [],
        selectedVersion: null,
        displayVersionHistoryModal: false,
        changeVersionLoading: false,
        versionKey: 0,
      };
    },
    computed: {
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      showMobileBurgerLinkField() {
        return this.showMobileLayoutField && this.form.mobileTemplate === HEADER_LOGO_LEFT_BURGER_TEMPLATE;
      },
    },
    beforeMount() {
      const isDuplicateForm = !this.isEdit && !!this.header;

      this.form = this.header ? this.header : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }

      if (this.header && new Date(this.header.createdAt) < new Date(HEADERS_TEMPLATES_UPDATES_DATE)) {
        this.desktopTemplateOptions = [...HEADER_TEMPLATES, ...HEADER_TEMPLATES_OLD];
      } else {
        this.form.mobileTemplate = this.header?.mobileTemplate || HEADER_MOBILE_TEMPLATES[0].value;
      }

      if (isDuplicateForm) {
        this.form.pages = [];
        this.form.updatedAt = null;
        this.form.updatedBy = null;
      }

      if (!this.ability.can("view", "header blog desktop layouts")) {
        this.desktopTemplateOptions = this.desktopTemplateOptions.filter(item => !item.value.includes("blogs"));
      }

      this.mainContentToShow = this.header?.customContent || "";
      this.form.logo = this.header?.logo?.["@id"] || null;
      this.form.mobileLogo = this.header?.mobileLogo?.["@id"] || null;
      this.form.backgroundColor = removeAlpha(this.header?.backgroundColor) || "#FFFFFF";
      this.form.borderColor = removeAlpha(this.header?.borderColor) || "#FFFFFF";
      this.form.domain = this.header?.domain?.["@id"];
      this.oldDomain = this.header?.domain;
      this.oldPages = this.header?.pages || [];
      this.form.menu = this.header?.menu || {};
      this.form.template = this.header?.template || "logo_left_content";
      this.form.menu.domain = this.form.domain;
      this.form.menuItemColor = removeAlpha(this.header?.menuItemColor) || "#B6B6B6";
      this.form.hoverColor = removeAlpha(this.header?.hoverColor) || "#FFFFFF";
      this.form.hoverBackgroundColor = removeAlpha(this.header?.hoverBackgroundColor) || "#FFFFFF";
      this.form.menu.menuItems = formatMenuItems(this.header?.menu.menuItems);
      if (this.isEdit) this.setVersions(this.header?.versions);
      this.form.mobileCustomContent = this.header?.mobileCustomContent || "";
      this.form.mobileBurgerLink = this.header?.mobileBurgerLink || "";
      if (this.header?.customContent)
        this.updateCustomContent(convertAssetsPathToAbsoluteLinks(this.header.customContent));
      if (this.header?.mobileCustomContent)
        this.updateMobileContent(convertAssetsPathToAbsoluteLinks(this.header.mobileCustomContent));

      //Check if it is not edit page and not create new page
      if (!this.isEdit && !!this.header) {
        delete this.form.menu["@id"];
        delete this.form.menu.id;
        delete this.form.menu["@type"];

        //This conditional have to be here because menu could be without menuItems
        if (this.form.menu.menuItems) {
          this.form.menu.menuItems.map(item => {
            delete item["@id"];
            delete item["@type"];
          });
        }
      }

      if (HEADER_BLOG_TEMPLATES_VALUES.includes(this.form.template)) {
        this.showCustomContentField = false;
        this.showMobileLayoutField = false;
      } else {
        this.showMenuField = false;
        this.form.menu = null;
      }

      this.showMobileCustomContentField = this.form.mobileTemplate === HEADER_CONTENT_ONLY_TEMPLATE;
    },
    methods: {
      handleAfterSave(data) {
        this.afterSave();
        if (data?.versions) this.setVersions(data.versions);
      },
      setVersions(versions, versionInUse = versions?.[0]?.version) {
        if (!versions?.length) return;

        this.versions = versions;

        this.versionsOptions = versions.map(versionItem => ({
          text: versionItem.updatedBy
            ? this.$t("resources.pages.saved_by_full", {
                date: getFullDateAndTime(versionItem.date),
                name: versionItem.updatedBy,
              })
            : getFullDateAndTime(versionItem.date),
          value: versionItem.version,
          disabled: versionItem.version === versionInUse,
        }));
      },
      async confirmVersionChange() {
        await this.changeVersion(this.selectedVersion);
        this.cancelVersionChange();
      },
      cancelVersionChange() {
        this.displayVersionHistoryModal = false;
        this.selectedVersion = null;
      },
      async changeVersion(version) {
        try {
          this.changeVersionLoading = true;
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `headers/${this.header.id}/version/${version}`,
          );

          const isBlogTemplate = HEADER_BLOG_TEMPLATES_VALUES.includes(data.template);
          this.form.name = data.name;
          this.form.template = data.template;
          this.form.mobileTemplate = data.mobileTemplate ?? null;
          this.form.logoPosition = data.logoPosition ?? null;
          this.form.mobileBurgerLink = data.mobileBurgerLink || "";
          this.form.logo = data.logo?.["@id"] || null;
          this.form.mobileLogo = data.mobileLogo?.["@id"] || null;
          this.form.backgroundColor = removeAlpha(data.backgroundColor) || "#FFFFFF";
          this.form.borderColor = removeAlpha(data.borderColor) || "#FFFFFF";
          this.form.menuItemColor = removeAlpha(data.menuItemColor) || "#B6B6B6";
          this.form.hoverColor = removeAlpha(data.hoverColor) || "#FFFFFF";
          this.form.hoverBackgroundColor = removeAlpha(data.hoverBackgroundColor) || "#FFFFFF";
          this.updateCustomContent(convertAssetsPathToAbsoluteLinks(data.customContent || ""));
          this.updateMobileContent(convertAssetsPathToAbsoluteLinks(data.mobileCustomContent || ""));
          if (isBlogTemplate) {
            this.form.menu = {
              ...(this.form.menu || {}),
              domain: this.form.domain,
              menuItems: formatRestoredMenuItems(data.menu?.menuItems),
            };
          }
          // Inputs that mount from now on (new template fields, menu link rows) read their value from
          // va-form's item, so it has to be the restored version too.
          this.$refs.form.formState.item = this.form;
          this.versionKey += 1;
          this.setVersions(this.versions, version);

          this.showMenuField = isBlogTemplate;
          this.showCustomContentField = !isBlogTemplate;
          this.showMobileLayoutField = !isBlogTemplate;
          this.showMobileCustomContentField = data.mobileTemplate === HEADER_CONTENT_ONLY_TEMPLATE;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.changeVersionLoading = false;
        }
      },
      modifyBeforeSend(data) {
        data.pages = data.pages.map(page => page["@id"]);

        if (data.customContent) {
          const content = parseToHtml(data.customContent);
          serializeDynamicComponents(content);
          data.customContent = content.body.innerHTML;
        }

        if (data.mobileCustomContent) {
          const content = parseToHtml(data.mobileCustomContent);
          serializeDynamicComponents(content);
          data.mobileCustomContent = content.body.innerHTML;
        }

        return data;
      },
      updateCustomContent(newValue) {
        this.mainContentToShow = newValue;
        let htmlElementFromContent = parseToHtml(this.mainContentToShow);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.customContent = htmlElementFromContent.body.innerHTML;
      },
      domainChanged(domain) {
        if (this.form.menu) {
          this.form.menu.domain = domain;

          if (this.form.menu.menuItems) {
            this.form.menu.menuItems.forEach(item => {
              item.page = null;
              item.category = null;
            });
          }
        }
        if (this.oldDomain !== domain) {
          this.form.pages = [];
        } else {
          this.form.pages = this.oldPages;
        }
      },
      updateContent(newValue) {
        this.mainContentToShow = newValue;
        let htmlElementFromContent = parseToHtml(this.mainContentToShow);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.customContent = htmlElementFromContent.body.innerHTML;
      },
      updateMobileContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        cleanupTinymceContent(htmlElementFromContent);
        this.form.mobileCustomContent = htmlElementFromContent.body.innerHTML;
      },
      mobileTemplateChange(mobileTemplate) {
        this.showMobileCustomContentField = mobileTemplate === HEADER_CONTENT_ONLY_TEMPLATE;
      },
      headerTemplateChange(headerTemplate) {
        if (HEADER_BLOG_TEMPLATES_VALUES.includes(headerTemplate)) {
          this.form.menu = this.form.menu || {};
          this.form.menu.domain = this.form.menu.domain || this.form.domain;
          this.form.borderColor = "#000000";
          this.showMobileLayoutField = false;
          this.showCustomContentField = false;
          this.showMenuField = true;
        } else {
          this.form.borderColor = "#FFFFFF";
          this.showMobileLayoutField = true;
          this.showCustomContentField = true;
          this.showMenuField = false;
        }

        if (["default", "mobile_burger", "centred_logo"].includes(headerTemplate)) {
          this.form.logoPosition = HEADER_LOGO_POSITIONS[0].value;
          this.form.mobileTemplate = null;
        } else {
          this.form.mobileTemplate = HEADER_MOBILE_TEMPLATES[0].value;
          this.form.logoPosition = null;
        }
      },
      updateHeaderLogo(newValue) {
        if (newValue === "") {
          this.form.logo = null;
        } else {
          this.form.logo = newValue["@id"];
        }
      },
      updateHeaderMobileLogo(newValue) {
        if (newValue === "") {
          this.form.mobileLogo = null;
        } else {
          this.form.mobileLogo = newValue["@id"];
        }
      },

      submit() {
        if (this.form.customContent) {
          // If form is not empty prepare the shortcode image URLS, to be stripped
          // This functions strips absolute URL's from the image src attribute
          let htmlElementFromContent = convertAssetsPathToRelativeLinks(this.form.customContent);

          cleanupTinymceContent(htmlElementFromContent);

          this.form.customContent = htmlElementFromContent.body.innerHTML;
        }

        if (this.form.mobileCustomContent) {
          let htmlElementFromContent = convertAssetsPathToRelativeLinks(this.form.mobileCustomContent);

          cleanupTinymceContent(htmlElementFromContent);

          this.form.mobileCustomContent = htmlElementFromContent.body.innerHTML;
        }

        if (!HEADER_BLOG_TEMPLATES_VALUES.includes(this.form?.template)) {
          this.form.menu = null;
        } else if (this.form?.menu?.["@id"]) {
          // menu.domain value should be IRI
          this.form.menu.domain = this.form.domain;
        }

        this.$refs.form.onSubmit();
      },
    },
    watch: {
      "form.menu.menuItems": {
        handler: function (newVal) {
          newVal?.forEach(item => {
            if (!item.action) {
              item.action = MENU_TAB_TYPES[0].value;
            }
          });
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .page-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-bottom: 12px;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }

  .page-chip-group {
    ::v-deep {
      .v-slide-group__content {
        padding: 0;
      }
    }
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;
  }

  .updated-at-label {
    @include text-caption-bold();
    color: get-color("subtitle");
  }

  .updated-at-time {
    @include text-body-2();
    color: get-color("subtitle");
  }
  .view-history {
    @include text-button-small();
    color: get-color("secondary-btn");
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: get-color("same-hover");
    }
  }
  .in-use-chip {
    height: 24px;
    &::v-deep {
      .v-chip__content {
        @include text-caption();
      }
    }
  }
</style>
